/// <amd-module name="Repos/Views/Branches/Provider/BranchTableProvider" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import { GitBranchStats, GitRepository, GitVersionDescriptor, GitVersionOptions, GitVersionType } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { IObservableArray, IObservableValue, IReadonlyObservableValue, ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { IGitRestClient } from "../RestClient/Git";
import { BranchTableProviderFetchResult, IBranchTableProvider } from "./BranchTableProvider.Types";

export class BranchTableProvider implements IBranchTableProvider {
    private compareBranch: EnhancedGitRef | undefined;
    private defaultBranch: EnhancedGitRef | undefined;
    private currentPage: number = 1;
    private hasMoreToFetch: boolean;
    private isUserSetCompareBranch: boolean | undefined;
    private readonly baseProviderInternal: IObservableArray<
        EnhancedGitRef | IReadonlyObservableValue<EnhancedGitRef | undefined>
    > = new ObservableArray([]);
    private readonly containedBranches = new Set<string>();
    private initialized: boolean = true;
    private readonly gitRestClient: IGitRestClient;
    private onFetchMore?: (page: number) => Promise<BranchTableProviderFetchResult>;
    private policyPaths: Set<string>;
    private readonly projectName: string;
    private readonly repository: GitRepository | undefined;
    private readonly repositoryName: string;
    private readonly routingService: IReposRoutingService;
    private readonly isTargetBranchUpdates2Enabled: boolean = false;

    constructor(
        initialItems: EnhancedGitRef[],
        initialCompareBranch: EnhancedGitRef | undefined,
        initialDefaultBranch: EnhancedGitRef | undefined,
        policyPaths: string[],
        reposRoutingService: IReposRoutingService,
        gitRestClient: IGitRestClient,
        repository: GitRepository | undefined,
        repositoryName: string,
        projectName: string,
        isTargetBranchUpdates2Enabled: boolean,
        loadingRows?: number,
        onFetchMore?: (page: number) => Promise<BranchTableProviderFetchResult>
    ) {
        this.routingService = reposRoutingService;
        this.gitRestClient = gitRestClient;
        this.repository = repository;
        this.repositoryName = repositoryName;
        this.projectName = projectName;
        this.isTargetBranchUpdates2Enabled = isTargetBranchUpdates2Enabled;

        this.initialize(initialItems, initialCompareBranch, initialDefaultBranch, policyPaths, onFetchMore);
        if (initialItems.length === 0) {
            this.baseProviderInternal.push(...new Array(loadingRows).fill(new ObservableValue(undefined)));
            this.initialized = false;
        }
    }

    public get baseProvider() {
        return this.baseProviderInternal;
    }

    public getCompareBranch() {
        return this.compareBranch;
    }

    public initialize = (
        branches: EnhancedGitRef[],
        compareBranch: EnhancedGitRef | undefined,
        defaultBranch: EnhancedGitRef | undefined,
        policyPaths: string[],
        onFetchMore?: (page: number) => Promise<BranchTableProviderFetchResult>,
        hasMoreToFetch?: boolean,
        isUserSetCompareBranch?: boolean
    ) => {
        this.initialized = true;
        this.compareBranch = compareBranch;
        this.defaultBranch = defaultBranch;

        this.policyPaths = new Set(policyPaths.map(refToBranchName));

        this.onFetchMore = onFetchMore;
        this.hasMoreToFetch = onFetchMore ? !!hasMoreToFetch : false;

        this.containedBranches.clear();
        branches.forEach(x => this.containedBranches.add(x.rootRef.name));
        // Do this towards the end so renders don't happen before we initialize other internal values
        this.baseProviderInternal.splice(0, this.baseProviderInternal.length, ...branches);

        if (this.hasMoreToFetch) {
            this.baseProviderInternal.push(new ObservableValue(undefined));
        }

        this.isUserSetCompareBranch = this.isUserSetCompareBranch || isUserSetCompareBranch;
    };

    public isInitialized = () => {
        return this.initialized;
    };

    public loadNextPage = () => {
        this.initialized &&
            this.onFetchMore &&
            this.hasMoreToFetch &&
            this.onFetchMore(this.currentPage).then(result => {
                this.hasMoreToFetch = result.hasMore;

                const branchesToAdd = result.data.filter(x => !this.containedBranches.has(x.rootRef.name));
                this.baseProviderInternal.removeAll(x => !ObservableLike.getValue(x));
                this.baseProviderInternal.push(...branchesToAdd);
                branchesToAdd.forEach(x => this.containedBranches.add(x.rootRef.name));

                if (result.hasMore) {
                    this.baseProviderInternal.push(new ObservableValue(undefined));
                }
            });

        this.currentPage += 1;
    };

    public pathHasPolicy = (path: string) => {
        if (this.policyPaths.has(path)) {
            return true;
        }

        while (path !== "") {
            // Add this to test parent folders
            if (this.policyPaths.has(path + "/*")) {
                return true;
            }

            path = path.substring(0, Math.max(0, path.lastIndexOf("/")));
        }

        return false;
    };

    public setCompareBranch(newCompareBranch: EnhancedGitRef, isUserSetting?: boolean) {
        if (isUserSetting) {
            this.isUserSetCompareBranch = true;
        }

        this.compareBranch = newCompareBranch;
        const newCompareBranchName = newCompareBranch.rootRef.name;
        const newCompareBranchDescriptor = branchToVersionDescriptor(refToBranchName(newCompareBranchName));

        const statsObservablesByCommitId: { [key: string]: IObservableValue<GitBranchStats | undefined>[] } = {};
        const versionDescriptorsForBranches: GitVersionDescriptor[] = [];

        this.modifyAll(item => {
            // Stats need to be recomputed so we clear them out
            item.stats.value = undefined;

            // Update external stores for fetching of stats
            if (statsObservablesByCommitId[item.rootRef.objectId]) {
                statsObservablesByCommitId[item.rootRef.objectId].push(item.stats);
            } else {
                statsObservablesByCommitId[item.rootRef.objectId] = [item.stats];
            }
            versionDescriptorsForBranches.push({
                version: item.rootRef.objectId,
                versionType: GitVersionType.Commit,
                versionOptions: GitVersionOptions.None
            });

            const currentBranchDescriptor = branchToVersionDescriptor(refToBranchName(item.rootRef.name));

            const newCompareUrl = this.routingService.getBranchCompareUrl(
                this.repositoryName,
                this.projectName,
                newCompareBranchDescriptor,
                currentBranchDescriptor
            );

            const newReverseCompareUrl = this.routingService.getBranchCompareUrl(
                this.repositoryName,
                this.projectName,
                currentBranchDescriptor,
                newCompareBranchDescriptor
            );

            let createPullRequestUrl: string | undefined;
            if (this.isTargetBranchUpdates2Enabled) {
                createPullRequestUrl = this.routingService.getPullRequestCreateUrl(
                    this.repositoryName,
                    this.projectName,
                    refToBranchName(item.rootRef.name),
                    refToBranchName(newCompareBranchName)
                );
            }

            if (item.rootRef.name === newCompareBranchName) {
                return { ...item, isCompare: true, compareUrl: undefined, reverseCompareUrl: undefined, createPullRequestUrl: undefined };
            } else {
                return { ...item, isCompare: false, compareUrl: newCompareUrl, reverseCompareUrl: newReverseCompareUrl, createPullRequestUrl: createPullRequestUrl };
            }
        });

        // Fetch and update stats
        if (this.repository) {
            this.gitRestClient
                .getBranchStatsBatch(
                    {
                        baseCommit: {
                            version: newCompareBranch.rootRef.objectId,
                            versionType: GitVersionType.Commit,
                            versionOptions: GitVersionOptions.None
                        },
                        targetCommits: versionDescriptorsForBranches
                    },
                    this.repository.id,
                    this.projectName
                )
                .then(result => {
                    result.forEach(branchStat => {
                        if (statsObservablesByCommitId[branchStat.name]) {
                            statsObservablesByCommitId[branchStat.name].forEach(observable => (observable.value = branchStat));
                        }
                    });
                });
        }
    }

    public setDefaultBranch(newDefaultBranch: EnhancedGitRef) {

        if (this.defaultBranch) {
            const isCompare = this.compareBranch ? this.compareBranch.rootRef.name === this.defaultBranch.rootRef.name : false;
            const oldDefaultBranch: EnhancedGitRef = { ...this.defaultBranch, isDefault: false, isCompare: isCompare };
            this.update(oldDefaultBranch);

            if (this.compareBranch && this.compareBranch.rootRef.name === oldDefaultBranch.rootRef.name) {
                this.compareBranch = { ...this.compareBranch, isDefault: false };
            }
        }

        const newDefBranch: EnhancedGitRef = { ...newDefaultBranch, isDefault: true };
        this.defaultBranch = newDefBranch;
        if (this.compareBranch && this.defaultBranch.rootRef.name === this.compareBranch.rootRef.name) {
            this.compareBranch = newDefaultBranch;
        }
        this.update(newDefBranch);

        if (this.isTargetBranchUpdates2Enabled) {
            this.modifyAll(item => {
                let createPullRequestUrl: string | undefined;
                if (this.compareBranch && item.rootRef.name !== this.compareBranch.rootRef.name) {
                    createPullRequestUrl = this.routingService.getPullRequestCreateUrl(
                        this.repositoryName,
                        this.projectName,
                        refToBranchName(item.rootRef.name),
                        refToBranchName(this.compareBranch.rootRef.name)
                    );
                }

                return { ...item, createPullRequestUrl: createPullRequestUrl };
            });
        } else {
            this.modifyAll(item => {
                return {
                    ...item,
                    createPullRequestUrl: this.routingService.getPullRequestCreateUrl(
                        this.repositoryName,
                        this.projectName,
                        refToBranchName(newDefBranch.rootRef.name),
                        refToBranchName(item.rootRef.name)
                    )
                };
            });
        }

        // setting default also sets compare if not user set compare
        if (!this.isUserSetCompareBranch) {
            this.setCompareBranch(newDefBranch);
        }
    }

    public remove(deletedBranch: EnhancedGitRef) {
        const index = this.getBranchIndex(deletedBranch);
        if (index !== -1) {
            this.baseProviderInternal.splice(index, 1);
        }
    }

    public update(updatedBranch: EnhancedGitRef) {
        const index = this.getBranchIndex(updatedBranch);
        if (index !== -1) {
            this.baseProviderInternal.change(index, updatedBranch);
        }
    }

    private getBranchIndex(branch: EnhancedGitRef) {
        return this.baseProvider.value.findIndex(x => {
            const data = ObservableLike.getValue(x);
            return data && data.rootRef.name === branch.rootRef.name;
        });
    }

    private modifyAll = (visitor: (item: EnhancedGitRef) => EnhancedGitRef) => {
        const newItems: (EnhancedGitRef | IReadonlyObservableValue<EnhancedGitRef | undefined>)[] = [];
        this.baseProviderInternal.value.forEach(tableItem => {
            const itemData = ObservableLike.getValue(tableItem);

            if (itemData) {
                newItems.push(visitor(itemData));
            } else {
                newItems.push(tableItem);
            }
        });

        this.baseProviderInternal.change(0, ...newItems);
    };
}
